"use client";

import { FunctionComponent, ReactElement, ReactNode } from "react";
import { TailwindColors } from "@components/common/types/general";
import { Body, CloseWrapper, Icon, IconWrapper, Message, Wrapper } from "./alert.styles";
import { getComponentColorProp, getIconByType } from "./alert.utils";
import { Close } from "../close/close";

export interface AlertProps {
    /**
     * Type of the alert.
     */
    type?: "error" | "warning" | "info" | "success";

    /**
     * Color of the alert.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Colorize only icon.
     */
    colorizeOnlyIcon?: boolean;

    /**
     * Show icon of the alert.
     */
    showIcon?: boolean;

    /**
     * A callback function which will be fired on click to close icon.
     */
    onClose?: () => void;

    /**
     * Whether the close icon should be shown or not.
     */
    showCloseIcon?: boolean;

    /**
     * Icon of the alert.
     */
    icon?: ReactNode;

    /**
     * Will display the alert with shadow.
     */
    shadow?: boolean;

    /**
     * Message of the alert.
     */
    message: ReactElement | string;

    /**
     * Border radius of the alert.
     */
    rounded?: "md" | "lg";

    /**
     * Class name of the alert.
     */
    className?: string;
}

export const Alert: FunctionComponent<AlertProps> = ({
    type,
    color,
    showIcon = true,
    onClose,
    showCloseIcon = true,
    colorizeOnlyIcon = false,
    shadow = false,
    icon,
    message,
    rounded,
    className,
}) => {
    return (
        <Wrapper color={color} shadow={shadow} colorizeOnlyIcon={colorizeOnlyIcon} rounded={rounded} className={className}>
            <Body>
                {showIcon && (
                    <IconWrapper>
                        <Icon color={color}>{!type && icon ? icon : getIconByType(type)}</Icon>
                    </IconWrapper>
                )}
                {message && <Message>{message}</Message>}
                {showCloseIcon && (
                    <CloseWrapper>
                        <Close background={false} color={getComponentColorProp(color)} onClick={onClose} />
                    </CloseWrapper>
                )}
            </Body>
        </Wrapper>
    );
};
